import React, { useEffect, useState, useCallback } from "react";
import api, { humanizeAxiosError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import SignatureCapture from "@/components/SignatureCapture";
import { Printer, Plus, X, FileImage, CalendarCheck, Receipt, FilePdf } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL || "";
import { parseLocalDate, fmtLocalDate } from "@/lib/utils";

const STATUS_LABEL = {
  pending_manager: { label: "Müdür Onayı Bekliyor", variant: "secondary" },
  pending_admin: { label: "Admin Onayı Bekliyor", variant: "secondary" },
  pending_board: { label: "Yıllık İzin Kurulu Bekliyor", variant: "secondary" },
  pending_staff_ack: { label: "Personel İmzası Bekliyor", variant: "secondary" },
  approved: { label: "Onaylandı", variant: "default" },
  rejected: { label: "Reddedildi", variant: "destructive" },
  cancelled: { label: "İptal Edildi", variant: "destructive" },
};

// Small badge shown in the "Belge" column reflecting the LLM cross-check.
const DOC_VERIFY_STYLES = {
  verified: { label: "Doğrulandı", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40" },
  warning: { label: "Uyarı", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40" },
  mismatch: { label: "Tutarsız", cls: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/40" },
  running: { label: "Doğrulanıyor…", cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/40 animate-pulse" },
  skipped: { label: "Atlandı", cls: "bg-muted text-muted-foreground border-border" },
  error: { label: "Hata", cls: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/40" },
  inconclusive: { label: "Belirsiz", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/40" },
};

// Kullanıcı, izin aralığındaki her "düşülmeyen" gün için türünü belirtir.
const OFF_DATE_TYPES = [
  { value: "Hafta Tatili", label: "Hafta Tatili" },
  { value: "Resmi Tatil", label: "Resmi Tatil" },
  { value: "Ücretli Mazeret İzni", label: "Ücretli Mazeret İzni" },
];


function DocVerificationBadge({ verdict }) {
  if (!verdict) {
    return <span className="text-xs text-muted-foreground border border-dashed border-border rounded px-2 py-0.5">Bekliyor</span>;
  }
  const cfg = DOC_VERIFY_STYLES[verdict.status] || { label: verdict.status, cls: "bg-muted" };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] border ${cfg.cls}`}>
      {cfg.label}
      {(verdict.flags?.length || 0) > 0 && (
        <span className="text-[10px] opacity-70">({verdict.flags.length})</span>
      )}
    </span>
  );
}


export default function Leaves() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [leaves, setLeaves] = useState([]);
  const [types, setTypes] = useState({});
  const [balance, setBalance] = useState(null);
  const [open, setOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(null); // {leave, mode: 'manager'|'admin'|'board'|'reject'}
  const [printData, setPrintData] = useState(null);
  const [convertOpen, setConvertOpen] = useState(null); // leave to convert (emergency)
  const [convertForm, setConvertForm] = useState({ new_leave_type: "bereavement", start_date: "", end_date: "", day_count: null, reason: "" });
  // Personel adına açılacak izin (Müdür/İK) — regular form ile aynı özellikler
  const [onBehalfOpen, setOnBehalfOpen] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [onBehalfForm, setOnBehalfForm] = useState({ user_id: "", leave_type: "excuse", start_date: "", end_date: "", day_count: 1, reason: "", initiator_signature: "", off_dates: [], off_dates_labels: {}, includes_weekend_or_holiday: false, report_url: "", start_time: "", end_time: "" });
  const [obOffDateInput, setObOffDateInput] = useState("");
  const [obOffDateType, setObOffDateType] = useState("Hafta Tatili");
  const [obDayCountManual, setObDayCountManual] = useState(false);
  // Personel imza-onay dialog
  const [ackOpen, setAckOpen] = useState(null); // leave
  const [ackSig, setAckSig] = useState("");
  const [ackNote, setAckNote] = useState("");
  const [boardBulkOpen, setBoardBulkOpen] = useState(false);
  const [boardPending, setBoardPending] = useState([]);
  const [boardBulkLoading, setBoardBulkLoading] = useState(false);

  const [form, setForm] = useState({ leave_type: "annual", start_date: "", end_date: "", day_count: 1, reason: "", includes_weekend_or_holiday: false, off_dates: [], off_dates_labels: {}, signature: "", report_url: "", start_time: "", end_time: "" });
  const [offDateInput, setOffDateInput] = useState("");
  const [offDateType, setOffDateType] = useState("Hafta Tatili");
  const [dayCountManual, setDayCountManual] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [sig, setSig] = useState("");
  const [note, setNote] = useState("");
  const [reportUploading, setReportUploading] = useState(false);
  const [obReportUploading, setObReportUploading] = useState(false);
  const reportFileRef = React.useRef(null);
  const obReportFileRef = React.useRef(null);
  const [docVerifyOpen, setDocVerifyOpen] = useState(null);
  const [rerunning, setRerunning] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(null); // leave to cancel (HR only)
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [receiptUploadingId, setReceiptUploadingId] = useState(null);
  const receiptFileRef = React.useRef(null);
  const receiptTargetIdRef = React.useRef(null);
  const [receiptFilter, setReceiptFilter] = useState(false);

  // Leave types that require/allow a supporting document upload (JPG/JPEG)
  const DOC_REQUIRED_TYPES = ["report", "sick", "maternity", "parental", "paternity", "bereavement", "marriage"];
  const needsDocument = (leaveType) => DOC_REQUIRED_TYPES.includes(leaveType);

  const uploadReport = async (file, setter, uploadingSetter) => {
    if (!file) return;
    const ext = (file.name || "").split(".").pop()?.toLowerCase();
    if (!["jpg", "jpeg"].includes(ext)) {
      toast.error("Yalnızca .jpg veya .jpeg dosya yüklenebilir");
      return;
    }
    // 8 MB limit for phone photos
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Dosya boyutu 8 MB'ı geçemez");
      return;
    }
    uploadingSetter(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/leaves/upload-report", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setter(data.url);
      toast.success("Belge yüklendi");
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      uploadingSetter(false);
    }
  };

  // Advance payment (avans ödemesi) — Maaş/30 * gün. Görünür: herkes (personel dahil).
  const advancePreview = React.useMemo(() => {
    if (form.leave_type !== "annual") return 0;
    const salary = Number(user?.salary || 0);
    if (!user?.salary_approved || salary <= 0) return 0;
    return Math.round((salary / 30) * Number(form.day_count || 0) * 100) / 100;
  }, [form.leave_type, form.day_count, user?.salary, user?.salary_approved]);

  const load = useCallback(() => {
    api.get("/leaves").then((r) => setLeaves(r.data));
    api.get("/leaves/types").then((r) => setTypes(r.data));
    api.get("/leaves/my-balance").then((r) => setBalance(r.data)).catch(() => {});
    api.get("/calendar").then((r) => setCalendarEvents(r.data || [])).catch(() => {});
    if (["manager", "hr", "admin", "gm"].includes(user?.role)) {
      api.get("/personnel/directory").then((r) => setDirectory(r.data || [])).catch(() => {});
    }
  }, [user?.role]);
  useEffect(() => { load(); }, [load]);

  // Ödeme dekontu yükleme (Muhasebe Müdürü / Admin)
  const triggerReceiptUpload = (leaveId) => {
    receiptTargetIdRef.current = leaveId;
    receiptFileRef.current?.click();
  };
  const onReceiptFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const leaveId = receiptTargetIdRef.current;
    if (!file || !leaveId) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) { toast.error("Yalnızca PDF/JPG/JPEG/PNG kabul edilir"); return; }
    setReceiptUploadingId(leaveId);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/leaves/${leaveId}/receipt`, fd);
      toast.success("Ödeme dekontu yüklendi");
      load();
    } catch (err) { toast.error(humanizeAxiosError(err)); }
    finally { setReceiptUploadingId(null); }
  };

  // İK: onaylı yıllık izin formu + ödeme dekontu tek PDF indir
  const downloadFullPdf = async (leaveId) => {
    try {
      const r = await api.get(`/leaves/${leaveId}/full-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `yillik_izin_${leaveId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Form + dekont PDF indirildi");
    } catch (err) {
      if (err?.response?.data instanceof Blob) {
        try { const t = JSON.parse(await err.response.data.text()); toast.error(t.detail || "İndirilemedi"); }
        catch { toast.error("İndirilemedi"); }
      } else { toast.error(humanizeAxiosError(err)); }
    }
  };

  // Bildirimden gelen ?focus=<leave_id> — ilgili izin yüklenince detay/yazdırma
  // penceresini otomatik açar.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focus");
    if (!focusId || !leaves.length) return;
    const target = leaves.find((l) => l.id === focusId);
    if (target) {
      setPrintData(target);
      // URL'i temizle ki yenilemede tekrar açılmasın.
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [leaves]);

  // Compute a suggested 10-day block for the current year, avoiding weekends & holidays.
  const suggestedTenDayPlan = React.useMemo(() => {
    if (form.leave_type !== "annual" || !balance) return null;
    const myApproved = leaves.filter((l) => l.user_id === user?.id && l.leave_type === "annual" && (l.final_status === "approved" || l.status === "pending" || l.status === "pending_manager" || l.status === "pending_admin"));
    const alreadyHas10 = myApproved.some((l) => Number(l.day_count || 0) >= 10);
    if (alreadyHas10) return null;
    if ((balance.remaining ?? 0) < 10) return null;
    // Take from tomorrow, iterate forward looking for a 10-consecutive-day window that
    // does not overlap existing approved leaves and starts on a Monday-Thursday if possible.
    const holidays = new Set(calendarEvents.filter((e) => e.kind === "holiday").map((e) => (e.date || "").slice(0, 10)));
    const busy = new Set();
    myApproved.forEach((l) => {
      const s = parseLocalDate(l.start_date), e = parseLocalDate(l.end_date);
      if (!s || !e) return;
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) busy.add(fmtLocalDate(d));
    });
    const start = new Date();
    start.setDate(start.getDate() + 7); // suggest at least a week ahead
    const dayLimit = 180;
    for (let i = 0; i < dayLimit; i++) {
      const d = new Date(start); d.setDate(d.getDate() + i);
      // Prefer Monday-Wednesday start (weekday 1-3).
      if (d.getDay() < 1 || d.getDay() > 3) continue;
      const iso = fmtLocalDate(d);
      if (busy.has(iso) || holidays.has(iso)) continue;
      // build 10-day window (leave days only, weekends excluded from count).
      let leaveDaysCounted = 0;
      let cursor = new Date(d);
      let lastCounted = new Date(d);
      let ok = true;
      while (leaveDaysCounted < 10) {
        const c = fmtLocalDate(cursor);
        if (busy.has(c)) { ok = false; break; }
        if (cursor.getDay() !== 0 && cursor.getDay() !== 6 && !holidays.has(c)) {
          leaveDaysCounted += 1;
          lastCounted = new Date(cursor);
        }
        cursor.setDate(cursor.getDate() + 1);
        if ((cursor - d) / 86400000 > 21) { ok = false; break; }
      }
      if (!ok) continue;
      // İş başı günü = son izinli günün ertesi (yeni semantic: end_date = iş başı, exclusive).
      const returnDay = new Date(lastCounted); returnDay.setDate(returnDay.getDate() + 1);
      return { start: iso, return: fmtLocalDate(returnDay) };
    }
    return null;
  }, [form.leave_type, balance, leaves, user?.id, calendarEvents]);

  const applyTenDayPlan = () => {
    if (!suggestedTenDayPlan) return;
    setForm((f) => ({ ...f, start_date: suggestedTenDayPlan.start, end_date: suggestedTenDayPlan.return, day_count: 10 }));
    setDayCountManual(false);
  };

  // 10 gün kuralı / eksi bakiye uyarısı (engelleme yok — bilgilendirme).
  const annualRuleWarning = React.useMemo(() => {
    if (form.leave_type !== "annual" || !balance) return null;
    const myAnnual = leaves.filter((l) => l.user_id === user?.id && l.leave_type === "annual" && (l.final_status === "approved" || l.final_status === "pending"));
    const chunks = myAnnual.map((l) => Number(l.day_count || 0));
    const dc = Number(form.day_count || 0);
    const hasTen = chunks.some((c) => c >= 10) || dc >= 10;
    const balanceAfter = Math.round(((balance.total_entitled ?? 0) - (balance.used ?? 0) - dc) * 100) / 100;
    const negative = balanceAfter < 0;
    const tenWarn = !hasTen && balanceAfter < 10;
    if (!tenWarn && !negative) return null;
    return { tenWarn, negative, balanceAfter };
  }, [form.leave_type, form.day_count, balance, leaves, user?.id]);

  // Auto-compute day_count from range - off_dates (only if user hasn't overridden it manually).
  useEffect(() => {
    if (dayCountManual) return;
    if (form.leave_type === "hourly") return;
    if (!form.start_date || !form.end_date) return;
    const s = parseLocalDate(form.start_date);
    const e = parseLocalDate(form.end_date);
    if (!s || !e || e <= s) return;
    // end_date is "İş Başı Tarihi" (return-to-work day, exclusive) → count days s .. e-1
    const totalDays = Math.round((e - s) / 86400000);
    const offInRange = (form.off_dates || []).filter((d) => {
      const dd = parseLocalDate(d);
      return dd && dd >= s && dd < e;
    }).length;
    const computed = Math.max(0.5, totalDays - offInRange);
    if (computed !== form.day_count) setForm((f) => ({ ...f, day_count: computed }));
  }, [form.start_date, form.end_date, form.off_dates, form.leave_type, dayCountManual]);

  const addOffDate = () => {
    if (!offDateInput) return;
    if (!offDateType) { toast.error("Gün türünü seçin"); return; }
    const s = form.start_date ? parseLocalDate(form.start_date) : null;
    const e = form.end_date ? parseLocalDate(form.end_date) : null;
    const d = parseLocalDate(offDateInput);
    if (s && e && (!d || d < s || d >= e)) {
      toast.error("Tarih izin aralığında olmalı");
      return;
    }
    if ((form.off_dates || []).includes(offDateInput)) {
      setOffDateInput("");
      return;
    }
    setForm((f) => ({
      ...f,
      off_dates: [...(f.off_dates || []), offDateInput].sort(),
      off_dates_labels: { ...(f.off_dates_labels || {}), [offDateInput]: offDateType },
      includes_weekend_or_holiday: true,
    }));
    setOffDateInput("");
  };

  const removeOffDate = (d) => {
    setForm((f) => {
      const labels = { ...(f.off_dates_labels || {}) };
      delete labels[d];
      return { ...f, off_dates: (f.off_dates || []).filter((x) => x !== d), off_dates_labels: labels };
    });
  };

  const weekdayLabel = (iso) => {
    const d = parseLocalDate(iso);
    if (!d) return "";
    return ["Pzr", "Pzt", "Sal", "Çrş", "Prş", "Cum", "Cmt"][d.getDay()];
  };

  const submit = async () => {
    if (!form.signature) { toast.error("İmzanızı ekleyin"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Tarihleri seçin"); return; }
    if (form.leave_type === "hourly" && (!form.start_time || !form.end_time)) {
      toast.error("Saatlik izin için başlangıç ve bitiş saatini girin"); return;
    }
    if (needsDocument(form.leave_type) && !form.report_url) {
      toast.error("Bu izin türü için belge (JPG/JPEG) yüklemeniz zorunludur");
      return;
    }
    if (form.leave_type === "annual" && annualRuleWarning) {
      let msg = "";
      if (annualRuleWarning.tenWarn) {
        msg += "Yıllık iznin bir parçası 10 günden az olmaması kuralı nedeniyle izniniz İnsan Kaynakları tarafından reddedilebilir.";
      }
      if (annualRuleWarning.negative) {
        msg += (msg ? "\n\n" : "") + `Bu talep yıllık izin bakiyenizi eksiye düşürüyor (kalan: ${annualRuleWarning.balanceAfter} gün). İnsan Kaynakları onayına bağlıdır.`;
      }
      msg += "\n\nYine de işleme devam etmek istiyor musunuz?";
      if (!window.confirm(msg)) return;
    }
    try {
      const payload = form.leave_type === "hourly"
        ? { ...form, end_date: form.start_date, day_count: 0, off_dates: [], off_dates_labels: {}, includes_weekend_or_holiday: false }
        : form;
      await api.post("/leaves", payload);
      toast.success("Talep gönderildi");
      setOpen(false);
      setForm({ leave_type: "annual", start_date: "", end_date: "", day_count: 1, reason: "", includes_weekend_or_holiday: false, off_dates: [], off_dates_labels: {}, signature: "", report_url: "", start_time: "", end_time: "" });
      setDayCountManual(false);
      setOffDateInput("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İzin talebi gönderilemedi");
    }
  };

  const doAction = async () => {
    const { leave, mode } = signOpen;
    // Signature is optional if the user has a saved signature on file (backend fallback).
    if (!sig && !user?.signature) { toast.error("İmzanızı ekleyin veya ayarlardan kayıtlı imza oluşturun"); return; }
    try {
      const body = { signature: sig || undefined, note };
      if (mode === "manager") await api.post(`/leaves/${leave.id}/manager-approve`, body);
      if (mode === "admin") await api.post(`/leaves/${leave.id}/admin-approve`, body);
      if (mode === "board") await api.post(`/leaves/${leave.id}/board-sign`, body);
      if (mode === "reject") await api.post(`/leaves/${leave.id}/reject`, body);
      toast.success("İşlem tamamlandı");
      setSignOpen(null); setSig(""); setNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const submitCancel = async () => {
    if (!cancelOpen) return;
    setCancelling(true);
    try {
      await api.post(`/leaves/${cancelOpen.id}/cancel`, { reason: cancelReason });
      toast.success("İzin iptal edildi, personele bildirim gönderildi");
      setCancelOpen(null); setCancelReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İptal edilemedi");
    } finally {
      setCancelling(false);
    }
  };

  const openConvert = (leave) => {
    setConvertForm({
      new_leave_type: "bereavement",
      start_date: leave.start_date?.slice(0, 10) || "",
      end_date: leave.end_date?.slice(0, 10) || "",
      day_count: Number(leave.day_count || 0),
      reason: "",
    });
    setConvertOpen(leave);
  };

  const submitConvert = async () => {
    if (!convertOpen) return;
    if (!convertForm.new_leave_type) { toast.error("Yeni izin türünü seçin"); return; }
    try {
      await api.post(`/leaves/${convertOpen.id}/convert`, convertForm);
      toast.success("İzin türü güncellendi ve ilgili kişilere bildirim gönderildi");
      setConvertOpen(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const submitOnBehalf = async () => {
    if (!onBehalfForm.user_id) { toast.error("Personel seçin"); return; }
    if (!onBehalfForm.start_date || !onBehalfForm.end_date) { toast.error("Tarihleri girin"); return; }
    if (!onBehalfForm.initiator_signature && !user?.signature) { toast.error("İmzanızı ekleyin veya ayarlardan kayıtlı imza oluşturun"); return; }
    if (onBehalfForm.leave_type === "hourly" && (!onBehalfForm.start_time || !onBehalfForm.end_time)) {
      toast.error("Saatlik izin için başlangıç ve bitiş saatini girin"); return;
    }
    if (needsDocument(onBehalfForm.leave_type) && !onBehalfForm.report_url) {
      toast.error("Bu izin türü için belge (JPG/JPEG) yüklemeniz zorunludur");
      return;
    }
    try {
      const base = onBehalfForm.leave_type === "hourly"
        ? { ...onBehalfForm, end_date: onBehalfForm.start_date, day_count: 0, off_dates: [], off_dates_labels: {}, includes_weekend_or_holiday: false }
        : onBehalfForm;
      const body = { ...base, initiator_signature: onBehalfForm.initiator_signature || undefined };
      await api.post("/leaves/on-behalf", body);
      toast.success("İzin talebi açıldı, personele bildirim gönderildi");
      setOnBehalfOpen(false);
      setOnBehalfForm({ user_id: "", leave_type: "excuse", start_date: "", end_date: "", day_count: 1, reason: "", initiator_signature: "", off_dates: [], off_dates_labels: {}, includes_weekend_or_holiday: false, report_url: "", start_time: "", end_time: "" });
      setObOffDateInput("");
      setObDayCountManual(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  // On-behalf: auto-compute day_count same as personal form
  useEffect(() => {
    if (obDayCountManual) return;
    if (onBehalfForm.leave_type === "hourly") return;
    if (!onBehalfForm.start_date || !onBehalfForm.end_date) return;
    const s = parseLocalDate(onBehalfForm.start_date);
    const e = parseLocalDate(onBehalfForm.end_date);
    if (!s || !e || e <= s) return;
    const totalDays = Math.round((e - s) / 86400000);
    const offInRange = (onBehalfForm.off_dates || []).filter((d) => {
      const dd = parseLocalDate(d);
      return dd && dd >= s && dd < e;
    }).length;
    const computed = Math.max(0.5, totalDays - offInRange);
    if (computed !== onBehalfForm.day_count) setOnBehalfForm((f) => ({ ...f, day_count: computed }));
     
  }, [onBehalfForm.start_date, onBehalfForm.end_date, onBehalfForm.off_dates, onBehalfForm.leave_type, obDayCountManual]);

  const addObOffDate = () => {
    if (!obOffDateInput) return;
    if (!obOffDateType) { toast.error("Gün türünü seçin"); return; }
    const s = onBehalfForm.start_date ? parseLocalDate(onBehalfForm.start_date) : null;
    const e = onBehalfForm.end_date ? parseLocalDate(onBehalfForm.end_date) : null;
    const d = parseLocalDate(obOffDateInput);
    if (s && e && (!d || d < s || d >= e)) { toast.error("Tarih izin aralığında olmalı"); return; }
    if ((onBehalfForm.off_dates || []).includes(obOffDateInput)) { setObOffDateInput(""); return; }
    setOnBehalfForm((f) => ({
      ...f,
      off_dates: [...(f.off_dates || []), obOffDateInput].sort(),
      off_dates_labels: { ...(f.off_dates_labels || {}), [obOffDateInput]: obOffDateType },
      includes_weekend_or_holiday: true,
    }));
    setObOffDateInput("");
  };
  const removeObOffDate = (d) => setOnBehalfForm((f) => {
    const labels = { ...(f.off_dates_labels || {}) };
    delete labels[d];
    return { ...f, off_dates: (f.off_dates || []).filter((x) => x !== d), off_dates_labels: labels };
  });

  const submitAck = async () => {
    if (!ackOpen) return;
    if (!ackSig && !user?.signature) { toast.error("İmzanızı ekleyin"); return; }
    try {
      await api.post(`/leaves/${ackOpen.id}/staff-ack`, { signature: ackSig || undefined, note: ackNote });
      toast.success("İmzanız alındı, izniniz onay sürecine devam ediyor");
      setAckOpen(null); setAckSig(""); setAckNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const openBoardBulk = async () => {
    setBoardBulkOpen(true);
    try {
      const r = await api.get("/leaves/board-pending");
      setBoardPending(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const submitBoardBulk = async () => {
    if (!user?.signature) return toast.error("Kayıtlı imzanız yok. Önce ayarlardan imza ekleyin.");
    if (!boardPending.length) return toast.error("Bekleyen kurul imzası yok");
    if (!window.confirm(`${boardPending.length} yıllık izin talebini kayıtlı imzanızla imzalıyorsunuz. Onaylıyor musunuz?`)) return;
    setBoardBulkLoading(true);
    try {
      const r = await api.post("/leaves/board-sign-bulk", {});
      toast.success(`${r.data.signed} izin imzalandı${r.data.completed ? ` (${r.data.completed} tamamen onaylandı)` : ""}`);
      setBoardBulkOpen(false);
      setBoardPending([]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    finally { setBoardBulkLoading(false); }
  };

  const canManageReceipts = ["accounting", "hr", "admin"].includes(user?.role);
  const pendingReceiptCount = canManageReceipts
    ? leaves.filter((l) => l.leave_type === "annual" && l.final_status === "approved" && !l.has_receipt).length
    : 0;
  const displayedLeaves = receiptFilter
    ? leaves.filter((l) => l.leave_type === "annual" && l.final_status === "approved" && !l.has_receipt)
    : leaves;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("leaves.title")}</div>
          <h1 className="font-display text-3xl font-bold mt-1">İzinler</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          {balance && (
            <div className="text-sm">
              <div className="uppercase text-xs text-muted-foreground">{t("leaves.annual")}</div>
              <div className="font-display font-bold text-2xl">{balance.remaining} <span className="text-sm font-normal text-muted-foreground">/ {balance.entitled} gün</span></div>
            </div>
          )}
          {balance?.next_anniversary_date && balance?.days_until_next_accrual != null && (
            <div
              className="rounded-lg border border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 text-xs leading-tight max-w-[220px]"
              data-testid="annual-timeline-card"
              title="Bir sonraki yıldönümünde yeni izin hakkınız hesabınıza eklenir."
            >
              <div className="uppercase tracking-wider text-[10px] text-emerald-700 dark:text-emerald-400 font-semibold flex items-center gap-1">
                <CalendarCheck size={12} /> {t("leaves.next_renewal")}
              </div>
              <div className="mt-1 text-emerald-900 dark:text-emerald-100">
                {balance.days_until_next_accrual === 0 ? (
                  <span className="font-semibold">{t("leaves.today_accrued", { days: balance.next_year_entitlement })}</span>
                ) : (
                  <>
                    <div className="font-display font-bold text-base">
                      {t("leaves.days_left", { n: balance.days_until_next_accrual })}
                    </div>
                    <div className="text-[11px] text-emerald-800 dark:text-emerald-300 mt-0.5">
                      {new Date(balance.next_anniversary_date).toLocaleDateString(({tr:"tr-TR", en:"en-US", id:"id-ID"}[lang] || "tr-TR"), { day: "numeric", month: "long", year: "numeric" })} · <b>{t("leaves.right_earned", { n: balance.next_year_entitlement })}</b>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2" data-testid="leaves-header-actions">
          {(user?.annual_leave_board || user?.role === "admin") && (
            <Button variant="outline" className="w-full sm:w-auto justify-center" onClick={openBoardBulk} data-testid="board-bulk-btn" title="Ay sonu kurul imzaları">
              📝 Bu Ayki Kurul İmzaları
            </Button>
          )}
          {["manager", "hr", "admin", "gm"].includes(user?.role) && (
            <Button variant="outline" className="w-full sm:w-auto justify-center" onClick={() => setOnBehalfOpen(true)} data-testid="on-behalf-btn">
              <Plus size={16} className="mr-1" />Personel Adına Talep
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="w-full sm:w-auto justify-center" data-testid="new-leave-btn"><Plus size={16} className="mr-1" />Yeni Talep</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{t("leaves.new_request")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>{t("leaves.type")}</Label>
                  <Select value={form.leave_type} onValueChange={(v) => setForm({...form, leave_type: v})}>
                    <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(types).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.leave_type === "annual" && suggestedTenDayPlan && (
                  <div className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="ten-day-suggestion">
                    <div className="text-xs uppercase tracking-wider text-primary font-semibold">Önerilen 10 Günlük Ana İzin</div>
                    <div className="text-sm">
                      Bu yıl için henüz 10 gün ve üzeri bir yıllık izin bloğunuz yok.
                      Önerilen tarih: <span className="font-semibold">{suggestedTenDayPlan.start}</span> — İş başı: <span className="font-semibold">{suggestedTenDayPlan.return}</span> (10 iş günü, hafta sonu ve resmi tatiller hariç).
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" onClick={applyTenDayPlan} data-testid="apply-ten-day-plan">Bu tarihleri kullan</Button>
                      <span className="text-[11px] text-muted-foreground self-center">İstediğiniz zaman değiştirebilirsiniz.</span>
                    </div>
                  </div>
                )}
                {form.leave_type === "annual" && annualRuleWarning && (
                  <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 space-y-1" data-testid="annual-rule-warning">
                    {annualRuleWarning.tenWarn && (
                      <div className="text-sm text-amber-700 dark:text-amber-400">
                        Yıllık iznin bir parçası 10 günden az olmaması kuralı nedeniyle izniniz İnsan Kaynakları tarafından reddedilebilir.
                      </div>
                    )}
                    {annualRuleWarning.negative && (
                      <div className="text-sm text-amber-700 dark:text-amber-400">
                        Bu talep yıllık izin bakiyenizi eksiye düşürüyor (kalan: <span className="font-semibold">{annualRuleWarning.balanceAfter}</span> gün). İnsan Kaynakları onayına bağlıdır.
                      </div>
                    )}
                    <div className="text-[11px] text-muted-foreground">Yine de talebi gönderebilirsiniz; onay İnsan Kaynaklarındadır.</div>
                  </div>
                )}
                {form.leave_type === "hourly" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label>Tarih <span className="text-destructive">*</span></Label>
                      <Input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value, end_date: e.target.value, day_count: 0})} data-testid="leave-start" />
                      <div className="text-[11px] text-muted-foreground mt-1">Saatlik izin aynı gün için talep edilir.</div>
                    </div>
                    <div><Label>Başlangıç Saati <span className="text-destructive">*</span></Label><Input type="time" value={form.start_time || ""} onChange={(e) => setForm({...form, start_time: e.target.value})} data-testid="leave-start-time" /></div>
                    <div><Label>Bitiş Saati <span className="text-destructive">*</span></Label><Input type="time" value={form.end_time || ""} onChange={(e) => setForm({...form, end_time: e.target.value})} data-testid="leave-end-time" /></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("leaves.start")}</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} data-testid="leave-start" /></div>
                    <div><Label>{t("leaves.return")}</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} data-testid="leave-end" /></div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label>Gün Sayısı {!dayCountManual && <span className="text-[10px] text-muted-foreground">(otomatik)</span>}</Label>
                        {dayCountManual && <button type="button" className="text-[10px] text-primary underline" onClick={() => setDayCountManual(false)}>Otomatiğe dön</button>}
                      </div>
                      <Input type="number" min={0.5} step={0.5} value={form.day_count} onChange={(e) => { setDayCountManual(true); setForm({...form, day_count: Number(e.target.value)}); }} data-testid="leave-days" />
                    </div>
                  </div>
                )}
                {form.leave_type !== "hourly" && (
                <div className="rounded-md border border-border p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Switch checked={form.includes_weekend_or_holiday} onCheckedChange={(v) => setForm({...form, includes_weekend_or_holiday: v})} data-testid="leave-weekend" />
                      <Label className="cursor-pointer">{t("leaves.includes_holiday")}</Label>
                    </div>
                    <span className="text-[10px] text-muted-foreground">İzinden düşülmez</span>
                  </div>
                  {form.includes_weekend_or_holiday && (
                    <>
                      <div className="flex gap-2 items-end flex-wrap">
                        <div className="flex-1 min-w-[130px]">
                          <Label className="text-xs">Tatil Tarihi</Label>
                          <Input type="date" value={offDateInput} onChange={(e) => setOffDateInput(e.target.value)} data-testid="off-date-input" min={form.start_date || undefined} max={form.end_date || undefined} />
                        </div>
                        <div className="flex-1 min-w-[150px]">
                          <Label className="text-xs">Gün Türü</Label>
                          <Select value={offDateType} onValueChange={setOffDateType}>
                            <SelectTrigger data-testid="off-date-type"><SelectValue placeholder="Tür seçin" /></SelectTrigger>
                            <SelectContent>
                              {OFF_DATE_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button type="button" onClick={addOffDate} size="sm" data-testid="off-date-add">Ekle</Button>
                      </div>
                      {(form.off_dates || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {form.off_dates.map((d) => (
                            <Badge key={d} variant="secondary" className="flex items-center gap-1 pr-1" data-testid={`off-chip-${d}`}>
                              <span className="text-xs">{d} · {weekdayLabel(d)}{form.off_dates_labels?.[d] ? ` — ${form.off_dates_labels[d]}` : ""}</span>
                              <button type="button" onClick={() => removeOffDate(d)} className="hover:bg-muted rounded-sm p-0.5" data-testid={`off-remove-${d}`}>
                                <X size={11} />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="text-[11px] text-muted-foreground">
                        Eklediğiniz {(form.off_dates || []).length} tarih yıllık iznin gün sayısına dahil edilmez. Her tarih için türünü (Hafta Tatili / Resmi Tatil / Ücretli Mazeret İzni) seçin.
                      </div>
                    </>
                  )}
                </div>
                )}
                <div><Label>Açıklama</Label><Textarea value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} data-testid="leave-reason" /></div>
                {needsDocument(form.leave_type) && (
                  <div className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="leave-doc-block">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs uppercase tracking-wider text-primary font-semibold">
                        Belge (JPG / JPEG) — Zorunlu
                      </div>
                      {form.report_url && (
                        <Badge variant="secondary" className="gap-1"><FileImage size={12} /> Yüklendi</Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground leading-relaxed">
                      {form.leave_type === "report" && "Doktor raporunuzun fotoğrafını ekleyin."}
                      {form.leave_type === "sick" && "Doktor raporunuzu / muayene belgesini ekleyin."}
                      {form.leave_type === "maternity" && "Doğum raporunuzu / hastane belgesini ekleyin."}
                      {form.leave_type === "parental" && "Doğum raporunuzu / hastane belgesini ekleyin."}
                      {form.leave_type === "paternity" && "Doğum belgesinin (çocuğun kimlik / hastane çıkış) fotoğrafını ekleyin."}
                      {form.leave_type === "marriage" && "Evlilik cüzdanının / nüfus belgesinin fotoğrafını ekleyin."}
                      {form.leave_type === "bereavement" && "Ölüm belgesinin (defin ruhsatı / hastane) fotoğrafını ekleyin."}
                    </div>
                    <input
                      ref={reportFileRef}
                      type="file"
                      accept=".jpg,.jpeg,image/jpeg"
                      className="hidden"
                      onChange={(e) => uploadReport(e.target.files?.[0], (url) => setForm((f) => ({ ...f, report_url: url })), setReportUploading)}
                      data-testid="leave-report-input"
                    />
                    <div className="flex gap-2 flex-wrap items-center">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={reportUploading}
                        onClick={() => reportFileRef.current?.click()}
                        data-testid="leave-report-upload-btn"
                      >
                        {reportUploading ? "Yükleniyor…" : (form.report_url ? "Yeniden Yükle" : "Belge Yükle")}
                      </Button>
                      {form.report_url && (
                        <>
                          <a
                            href={`${API}${form.report_url}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-primary underline"
                            data-testid="leave-report-preview"
                          >
                            Belgeyi Görüntüle
                          </a>
                          <button
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, report_url: "" }))}
                            className="text-xs text-destructive underline"
                            data-testid="leave-report-remove"
                          >
                            Kaldır
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {form.leave_type === "annual" && advancePreview > 0 && (
                  <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-2" data-testid="advance-preview">
                    <div className="text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold">{t("leaves.advance_payment")}</div>
                    <div className="text-sm">
                      Günlük ücret: <span className="font-semibold">{(Number(user?.salary || 0) / 30).toFixed(2)} ₺</span> × <span className="font-semibold">{form.day_count} gün</span> = <span className="font-display font-bold text-lg">{advancePreview.toLocaleString("tr-TR")} ₺</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground">İzne çıkmadan önce ödenecek net avans tutarı. (Maaş / 30 × gün)</div>
                    {(user?.iban || user?.bank_name) ? (
                      <div className="mt-2 border-t border-emerald-500/20 pt-2 space-y-0.5" data-testid="advance-iban-info">
                        <div className="text-[11px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold">{t("leaves.payment_account")}</div>
                        {user?.bank_name && <div className="text-xs">Banka: <span className="font-semibold">{user.bank_name}</span></div>}
                        {user?.iban && <div className="text-xs font-mono">{user.iban.replace(/(.{4})/g, "$1 ").trim()}</div>}
                        {user?.account_holder_name && <div className="text-[11px] text-muted-foreground">Hesap Sahibi: {user.account_holder_name}</div>}
                      </div>
                    ) : (
                      <div className="mt-2 border-t border-emerald-500/20 pt-2 rounded-sm bg-amber-500/10 border border-amber-500/40 p-2 text-[11px] text-amber-800 dark:text-amber-300" data-testid="advance-iban-missing">
                        Sistemde kayıtlı bir IBAN bilginiz yok. İK'dan IBAN bilgilerinizin eklenmesini rica edin, aksi hâlde avans ödemesi gerçekleştirilemez.
                      </div>
                    )}
                  </div>
                )}
                {form.leave_type === "annual" && !user?.salary_approved && (
                  <div className="rounded-md border border-warning/40 bg-warning/10 p-2 text-xs text-warning-foreground/80" data-testid="advance-preview-warn">
                    Maaşınız İK tarafından henüz onaylanmadığı için avans tutarı hesaplanamadı.
                  </div>
                )}
                <div><Label>İmza</Label><SignatureCapture onChange={(s) => setForm((f) => ({...f, signature: s}))} testId="leave-signature" /></div>
                <div className="flex justify-end"><Button onClick={submit} data-testid="leave-submit">Gönder</Button></div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {canManageReceipts && (
            <div className="flex items-center justify-between gap-2 flex-wrap px-4 py-3 border-b border-border">
              <div className="text-sm font-medium flex items-center gap-2">
                <Receipt size={18} className="text-primary" />
                Ödeme Dekontu Takibi
                {pendingReceiptCount > 0 && (
                  <Badge variant="secondary" data-testid="pending-receipt-count">{pendingReceiptCount} bekleyen</Badge>
                )}
              </div>
              <Button
                size="sm"
                variant={receiptFilter ? "default" : "outline"}
                onClick={() => setReceiptFilter((v) => !v)}
                data-testid="receipt-filter-toggle"
              >
                {receiptFilter ? "Tümünü Göster" : "Dekontu Yüklenmemiş Yıllık İzinler"}
              </Button>
            </div>
          )}
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="text-muted-foreground text-xs uppercase tracking-wider bg-muted/50">
                <tr>
                  <th className="text-left p-3">Personel</th><th className="text-left p-3">Tür</th><th className="text-left p-3">Başlangıç</th><th className="text-left p-3">İş Başı</th>
                  <th className="text-left p-3">Gün</th><th className="text-left p-3">Tatil</th><th className="text-left p-3">Durum</th><th className="text-left p-3">Belge</th><th className="text-left p-3">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {displayedLeaves.map((l) => (
                  <tr key={l.id} className="border-t border-border">
                    <td className="p-3">{l.user_name}</td>
                    <td className="p-3">{types[l.leave_type]?.label || l.leave_type}</td>
                    <td className="p-3">{l.start_date?.slice(0,10)}</td>
                    <td className="p-3">{l.end_date?.slice(0,10)}</td>
                    <td className="p-3">{l.day_count}</td>
                    <td className="p-3">{l.includes_weekend_or_holiday ? "Evet" : "-"}</td>
                    <td className="p-3"><Badge variant={STATUS_LABEL[l.status]?.variant || "outline"}>{STATUS_LABEL[l.status]?.label || l.status}</Badge>
                      {canManageReceipts && l.leave_type === "annual" && l.final_status === "approved" && (
                        l.has_receipt ? (
                          <Badge className="ml-1 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/40" data-testid={`receipt-status-${l.id}`}>Dekont ✓</Badge>
                        ) : (
                          <Badge className="ml-1 bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/40" data-testid={`receipt-status-${l.id}`}>Dekont bekleniyor</Badge>
                        )
                      )}
                    </td>
                    <td className="p-3">
                      {l.report_url ? (
                        <button
                          type="button"
                          onClick={() => setDocVerifyOpen(l)}
                          className="flex items-center gap-1"
                          data-testid={`doc-verify-${l.id}`}
                          title="Belge doğrulama sonucunu göster"
                        >
                          <DocVerificationBadge verdict={l.doc_verification} />
                        </button>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1 flex-wrap">
                        {l.status === "pending_staff_ack" && l.user_id === user?.id && (
                          <Button size="sm" onClick={() => { setAckOpen(l); setAckSig(""); setAckNote(""); }} data-testid={`staff-ack-${l.id}`}>
                            İmzala
                          </Button>
                        )}
                        {["manager", "hr", "admin"].includes(user?.role) && l.status === "pending_manager" && l.user_id !== user?.id && (
                          <Button size="sm" variant="outline" onClick={() => setSignOpen({leave: l, mode: "manager"})} data-testid={`mgr-approve-${l.id}`}>{t("leaves.manager_approval")}</Button>
                        )}
                        {(user?.role === "admin" || user?.role === "hr") && ["pending_admin", "pending_board"].includes(l.status) && l.user_id !== user?.id && (
                          <Button size="sm" variant="outline" onClick={() => setSignOpen({leave: l, mode: "admin"})} data-testid={`admin-approve-${l.id}`}>Admin Onay</Button>
                        )}
                        {user?.annual_leave_board && l.leave_type === "annual" && ["pending_board", "pending_admin"].includes(l.status) && (
                          <Button size="sm" variant="outline" onClick={() => setSignOpen({leave: l, mode: "board"})} data-testid={`board-sign-${l.id}`}>Kurul İmzası</Button>
                        )}
                        {["admin", "hr", "gm"].includes(user?.role) && (l.final_status === "pending" || l.final_status === "approved") && (
                          <Button size="sm" variant="ghost" onClick={() => openConvert(l)} data-testid={`convert-${l.id}`} title="İzin türünü değiştir (ölüm/babalık/analık acil)">
                            Acil Güncelle
                          </Button>
                        )}
                        {["admin", "hr", "manager"].includes(user?.role) && l.final_status === "pending" && (
                          <Button size="sm" variant="ghost" onClick={() => setSignOpen({leave: l, mode: "reject"})}>Reddet</Button>
                        )}
                        {["admin", "hr"].includes(user?.role) && l.status !== "cancelled" && (
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => { setCancelOpen(l); setCancelReason(""); }} data-testid={`cancel-leave-${l.id}`} title="İzni iptal et (sadece İnsan Kaynakları)">
                            İptal Et
                          </Button>
                        )}
                        {l.report_url && (
                          <Button size="sm" variant="ghost" asChild data-testid={`view-doc-${l.id}`}>
                            <a href={`${API}${l.report_url}`} target="_blank" rel="noreferrer" title="Ekli belgeyi göster">
                              <FileImage size={16} />
                            </a>
                          </Button>
                        )}
                        {["admin", "hr", "manager", "accounting"].includes(user?.role) && (
                          <Button size="sm" variant="ghost" onClick={() => setPrintData(l)} data-testid={`print-${l.id}`}><Printer size={16} /></Button>
                        )}
                        {["accounting", "admin"].includes(user?.role) && l.leave_type === "annual" && l.final_status === "approved" && (
                          <Button size="sm" variant="outline" disabled={receiptUploadingId === l.id} onClick={() => triggerReceiptUpload(l.id)} data-testid={`upload-receipt-${l.id}`} title="Ödeme dekontu yükle (PDF/JPG/PNG)">
                            <Receipt size={16} className="mr-1" />{receiptUploadingId === l.id ? "Yükleniyor…" : (l.has_receipt ? "Dekont Yenile" : "Dekont Yükle")}
                          </Button>
                        )}
                        {l.payment_receipt_url && (
                          <Button size="sm" variant="ghost" asChild data-testid={`view-receipt-${l.id}`}>
                            <a href={`${API}${l.payment_receipt_url}`} target="_blank" rel="noreferrer" title={`Ödeme dekontu${l.receipt_uploaded_by_name ? " — " + l.receipt_uploaded_by_name : ""}`}>
                              <Receipt size={16} weight="fill" className="text-emerald-600" />
                            </a>
                          </Button>
                        )}
                        {["hr", "admin"].includes(user?.role) && l.leave_type === "annual" && l.final_status === "approved" && (
                          <Button size="sm" variant="ghost" onClick={() => downloadFullPdf(l.id)} data-testid={`full-pdf-${l.id}`} title="Onaylı form + ödeme dekontu (tek PDF) indir">
                            <FilePdf size={16} className="text-rose-600" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {displayedLeaves.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">{receiptFilter ? "Dekontu bekleyen onaylı yıllık izin yok" : "Kayıt yok"}</td></tr>}
              </tbody>
            </table>
          </div>
          <input ref={receiptFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={onReceiptFileChange} data-testid="receipt-file-input" />
        </CardContent>
      </Card>

      <Dialog open={!!signOpen} onOpenChange={(o) => !o && setSignOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>İmza ile Onay</DialogTitle></DialogHeader>
          {signOpen?.leave?.leave_type === "annual" && signOpen?.mode !== "reject" && (signOpen?.leave?.annual_ten_day_warning || signOpen?.leave?.annual_negative_balance) && (
            <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 space-y-1" data-testid="approval-annual-warning">
              <div className="text-xs uppercase tracking-wider text-amber-700 dark:text-amber-400 font-semibold">Dikkat</div>
              {signOpen.leave.annual_ten_day_warning && (
                <div className="text-sm text-amber-700 dark:text-amber-400">
                  Bu personelin bu yıl 10 gün ve üzeri bir yıllık izin bloğu bulunmuyor. Yıllık iznin bir parçasının en az 10 gün olması kuralı nedeniyle bu talep reddedilebilir.
                </div>
              )}
              {signOpen.leave.annual_negative_balance && (
                <div className="text-sm text-amber-700 dark:text-amber-400">
                  Bu talep personelin yıllık izin bakiyesini eksiye düşürüyor (kalan: <span className="font-semibold">{signOpen.leave.annual_balance_after}</span> gün).
                </div>
              )}
            </div>
          )}
          <Textarea placeholder="Not (opsiyonel)" value={note} onChange={(e) => setNote(e.target.value)} />
          <SignatureCapture onChange={setSig} testId="approval-signature" />
          <Button onClick={doAction} data-testid="approval-submit">Onayla</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelOpen} onOpenChange={(o) => !o && setCancelOpen(null)}>
        <DialogContent data-testid="cancel-leave-dialog">
          <DialogHeader><DialogTitle>İzni İptal Et</DialogTitle></DialogHeader>
          {cancelOpen && (
            <div className="space-y-3">
              <div className="text-sm rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <b>{cancelOpen.user_name}</b> — {types[cancelOpen.leave_type]?.label || cancelOpen.leave_type} · {cancelOpen.start_date?.slice(0,10)} → {cancelOpen.end_date?.slice(0,10)} · {cancelOpen.day_count} gün
                <div className="text-xs text-muted-foreground mt-1">Bu izin iptal edilecek ve personele bildirim gönderilecek. Bu işlem geri alınamaz.</div>
              </div>
              <div>
                <Label>İptal Sebebi (opsiyonel)</Label>
                <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Örn: Operasyonel ihtiyaç nedeniyle iptal edildi" data-testid="cancel-reason" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCancelOpen(null)}>Vazgeç</Button>
                <Button variant="destructive" onClick={submitCancel} disabled={cancelling} data-testid="cancel-leave-confirm">{cancelling ? "İptal ediliyor…" : "İzni İptal Et"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={boardBulkOpen} onOpenChange={setBoardBulkOpen}>
        <DialogContent className="max-w-lg" data-testid="board-bulk-dialog">
          <DialogHeader><DialogTitle>Bu Ayki Kurul İmzaları</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground rounded-md border border-primary/30 bg-primary/5 p-2">
              Personel + Müdür + İK onaylı yıllık izinler için kurul imzaları burada toplanır. Tek tıkla tüm bekleyenleri kayıtlı imzanızla imzalayabilirsiniz.
            </div>
            {boardPending.length === 0 ? (
              <div className="text-center text-muted-foreground py-6 text-sm">🎉 Bekleyen kurul imzası yok</div>
            ) : (
              <div className="rounded-md border border-border max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left p-2">Personel</th>
                      <th className="text-left p-2">Departman</th>
                      <th className="text-left p-2">Tarih</th>
                      <th className="text-right p-2">Gün</th>
                      <th className="text-center p-2">İmza</th>
                    </tr>
                  </thead>
                  <tbody>
                    {boardPending.map((l) => (
                      <tr key={l.id} className="border-t border-border">
                        <td className="p-2 font-medium">{l.user_name}</td>
                        <td className="p-2 text-xs">{l.department}</td>
                        <td className="p-2 text-xs">{l.start_date} → {l.end_date}</td>
                        <td className="p-2 text-right">{l.day_count}</td>
                        <td className="p-2 text-center">
                          <span className="text-[10px]" data-testid={`bp-count-${l.id}`}>{l.board_count}/3</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!user?.signature && (
              <div className="text-xs rounded-md border border-destructive/40 bg-destructive/10 p-2 text-destructive">
                Kayıtlı imzanız yok — önce Ayarlar'dan imzanızı ekleyin.
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setBoardBulkOpen(false)}>Kapat</Button>
              <Button
                onClick={submitBoardBulk}
                disabled={boardBulkLoading || boardPending.length === 0 || !user?.signature}
                data-testid="board-bulk-submit"
              >
                {boardBulkLoading ? "İmzalanıyor…" : `Tümünü İmzala (${boardPending.length})`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={onBehalfOpen} onOpenChange={setOnBehalfOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Personel Adına İzin Talebi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground rounded-md border border-primary/30 bg-primary/5 p-2">
              Talebi açtıktan sonra personelinize bildirim gider. Personel imzasını attıktan sonra süreç {user?.role === "manager" ? "İK/Admin onayına" : "otomatik olarak"} devam eder.
            </div>
            <div>
              <Label>Personel</Label>
              <Select value={onBehalfForm.user_id} onValueChange={(v) => setOnBehalfForm({...onBehalfForm, user_id: v})}>
                <SelectTrigger data-testid="ob-user"><SelectValue placeholder="Personel seçin" /></SelectTrigger>
                <SelectContent>
                  {directory
                    .filter((p) => user?.role !== "manager" || p.department === user?.department)
                    .filter((p) => p.id !== user?.id)
                    .sort((a, b) => (a.first_name || "").localeCompare(b.first_name || ""))
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.first_name} {p.last_name} — {p.department}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>İzin Türü</Label>
              <Select value={onBehalfForm.leave_type} onValueChange={(v) => setOnBehalfForm({...onBehalfForm, leave_type: v})}>
                <SelectTrigger data-testid="ob-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(types).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {onBehalfForm.leave_type === "hourly" ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Tarih <span className="text-destructive">*</span></Label>
                  <Input type="date" value={onBehalfForm.start_date} onChange={(e) => setOnBehalfForm({...onBehalfForm, start_date: e.target.value, end_date: e.target.value, day_count: 0})} data-testid="ob-start" />
                  <div className="text-[11px] text-muted-foreground mt-1">Saatlik izin aynı gün için talep edilir.</div>
                </div>
                <div><Label>Başlangıç Saati <span className="text-destructive">*</span></Label><Input type="time" value={onBehalfForm.start_time || ""} onChange={(e) => setOnBehalfForm({...onBehalfForm, start_time: e.target.value})} data-testid="ob-start-time" /></div>
                <div><Label>Bitiş Saati <span className="text-destructive">*</span></Label><Input type="time" value={onBehalfForm.end_time || ""} onChange={(e) => setOnBehalfForm({...onBehalfForm, end_time: e.target.value})} data-testid="ob-end-time" /></div>
              </div>
            ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Başlangıç</Label><Input type="date" value={onBehalfForm.start_date} onChange={(e) => setOnBehalfForm({...onBehalfForm, start_date: e.target.value})} data-testid="ob-start" /></div>
              <div><Label>İş Başı Tarihi</Label><Input type="date" value={onBehalfForm.end_date} onChange={(e) => setOnBehalfForm({...onBehalfForm, end_date: e.target.value})} data-testid="ob-end" /></div>
              <div>
                <Label>Gün</Label>
                <Input type="number" step={0.5} min={0.5} value={onBehalfForm.day_count}
                  onChange={(e) => { setObDayCountManual(true); setOnBehalfForm({...onBehalfForm, day_count: Number(e.target.value)}); }}
                  data-testid="ob-days" />
                <div className="text-[11px] text-muted-foreground mt-1">Tarihlerden otomatik hesaplanır. Manuel değiştirebilirsiniz.</div>
              </div>
            </div>
            )}
            {onBehalfForm.leave_type !== "hourly" && (
            <div className="rounded-md border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="ob-weekend-toggle" className="text-sm">Hafta tatili / resmi tatil içeriyor</Label>
                <Switch
                  id="ob-weekend-toggle"
                  checked={onBehalfForm.includes_weekend_or_holiday}
                  onCheckedChange={(v) => setOnBehalfForm({ ...onBehalfForm, includes_weekend_or_holiday: v })}
                  data-testid="ob-weekend-switch"
                />
              </div>
              {onBehalfForm.includes_weekend_or_holiday && (
                <>
                  <div className="text-[11px] text-muted-foreground">Aralıktaki hafta tatili / resmi tatil / ücretli mazeret günlerini ve türünü seçin — bu günler izin gününden düşülmez.</div>
                  <div className="flex gap-2 items-end flex-wrap">
                    <div className="flex-1 min-w-[130px]">
                      <Label className="text-xs">Tarih</Label>
                      <Input type="date" value={obOffDateInput} onChange={(e) => setObOffDateInput(e.target.value)} data-testid="ob-off-input" />
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <Label className="text-xs">Gün Türü</Label>
                      <Select value={obOffDateType} onValueChange={setObOffDateType}>
                        <SelectTrigger data-testid="ob-off-type"><SelectValue placeholder="Tür seçin" /></SelectTrigger>
                        <SelectContent>
                          {OFF_DATE_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="button" size="sm" onClick={addObOffDate} data-testid="ob-off-add"><Plus size={14} className="mr-1" />Ekle</Button>
                  </div>
                  {(onBehalfForm.off_dates || []).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {(onBehalfForm.off_dates || []).map((d) => (
                        <Badge key={d} variant="secondary" className="gap-1 pr-1">
                          <span className="text-xs">{d} · {weekdayLabel(d)}{onBehalfForm.off_dates_labels?.[d] ? ` — ${onBehalfForm.off_dates_labels[d]}` : ""}</span>
                          <button type="button" onClick={() => removeObOffDate(d)} className="hover:bg-muted rounded-sm p-0.5" data-testid={`ob-off-remove-${d}`}>
                            <X size={11} />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="text-[11px] text-muted-foreground">Eklenen {(onBehalfForm.off_dates || []).length} tarih izin gününe dahil edilmez.</div>
                </>
              )}
            </div>
            )}
            <div><Label>Açıklama</Label><Textarea value={onBehalfForm.reason} onChange={(e) => setOnBehalfForm({...onBehalfForm, reason: e.target.value})} data-testid="ob-reason" /></div>
            {needsDocument(onBehalfForm.leave_type) && (
              <div className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="ob-doc-block">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs uppercase tracking-wider text-primary font-semibold">Belge (JPG / JPEG) — Zorunlu</div>
                  {onBehalfForm.report_url && (<Badge variant="secondary" className="gap-1"><FileImage size={12} /> Yüklendi</Badge>)}
                </div>
                <input
                  ref={obReportFileRef}
                  type="file"
                  accept=".jpg,.jpeg,image/jpeg"
                  className="hidden"
                  onChange={(e) => uploadReport(e.target.files?.[0], (url) => setOnBehalfForm((f) => ({ ...f, report_url: url })), setObReportUploading)}
                  data-testid="ob-report-input"
                />
                <div className="flex gap-2 flex-wrap items-center">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={obReportUploading}
                    onClick={() => obReportFileRef.current?.click()}
                    data-testid="ob-report-upload-btn"
                  >
                    {obReportUploading ? "Yükleniyor…" : (onBehalfForm.report_url ? "Yeniden Yükle" : "Belge Yükle")}
                  </Button>
                  {onBehalfForm.report_url && (
                    <>
                      <a href={`${API}${onBehalfForm.report_url}`} target="_blank" rel="noreferrer" className="text-xs text-primary underline" data-testid="ob-report-preview">Belgeyi Görüntüle</a>
                      <button type="button" onClick={() => setOnBehalfForm((f) => ({ ...f, report_url: "" }))} className="text-xs text-destructive underline" data-testid="ob-report-remove">Kaldır</button>
                    </>
                  )}
                </div>
              </div>
            )}
            <div>
              <Label>İmzanız</Label>
              <div className="text-[11px] text-muted-foreground mb-1">Kayıtlı imzanız otomatik kullanılır. Farklı bir imza atmak için aşağıdaki alana çizebilirsiniz.</div>
              <SignatureCapture onChange={(s) => setOnBehalfForm((f) => ({...f, initiator_signature: s}))} testId="ob-signature" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOnBehalfOpen(false)}>Vazgeç</Button>
              <Button onClick={submitOnBehalf} data-testid="ob-submit">Talep Aç</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ackOpen} onOpenChange={(o) => !o && setAckOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>İzin Talebini İmzala</DialogTitle></DialogHeader>
          {ackOpen && (
            <div className="space-y-3">
              <div className="rounded-md bg-muted/50 p-3 text-sm">
                <div>{ackOpen.initiator_name} sizin için izin talebi açtı.</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Tür: <b>{types[ackOpen.leave_type]?.label}</b> · {ackOpen.start_date?.slice(0,10)} → {ackOpen.end_date?.slice(0,10)} · {ackOpen.day_count} gün
                </div>
                {ackOpen.reason && <div className="text-xs mt-1"><b>Sebep:</b> {ackOpen.reason}</div>}
              </div>
              <Textarea placeholder="Not (opsiyonel)" value={ackNote} onChange={(e) => setAckNote(e.target.value)} data-testid="ack-note" />
              <SignatureCapture onChange={setAckSig} testId="ack-signature" />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setAckOpen(null)}>Vazgeç</Button>
                <Button onClick={submitAck} data-testid="ack-submit">İmzala ve Onayla</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!convertOpen} onOpenChange={(o) => !o && setConvertOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Acil İzin Güncelleme</DialogTitle></DialogHeader>
          {convertOpen && (
            <div className="space-y-3">
              <div className="rounded-md bg-muted/50 p-3 text-sm">
                <div className="font-semibold">{convertOpen.user_name}</div>
                <div className="text-xs text-muted-foreground">
                  Mevcut tür: <b>{types[convertOpen.leave_type]?.label || convertOpen.leave_type}</b> · {convertOpen.start_date?.slice(0,10)} → {convertOpen.end_date?.slice(0,10)}
                </div>
              </div>
              <div>
                <Label>Yeni İzin Türü</Label>
                <Select value={convertForm.new_leave_type} onValueChange={(v) => setConvertForm({...convertForm, new_leave_type: v})}>
                  <SelectTrigger data-testid="convert-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(types).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Başlangıç</Label><Input type="date" value={convertForm.start_date} onChange={(e) => setConvertForm({...convertForm, start_date: e.target.value})} data-testid="convert-start" /></div>
                <div><Label>İş Başı Tarihi</Label><Input type="date" value={convertForm.end_date} onChange={(e) => setConvertForm({...convertForm, end_date: e.target.value})} data-testid="convert-end" /></div>
              </div>
              <div><Label>Gün Sayısı</Label><Input type="number" step={0.5} value={convertForm.day_count || 0} onChange={(e) => setConvertForm({...convertForm, day_count: Number(e.target.value)})} data-testid="convert-days" /></div>
              <div><Label>Açıklama</Label><Textarea value={convertForm.reason} onChange={(e) => setConvertForm({...convertForm, reason: e.target.value})} placeholder="Ölüm, babalık, analık vb. gerekçe" data-testid="convert-reason" /></div>
              <div className="text-[11px] text-muted-foreground">Personel ve müdürüne otomatik bildirim gönderilecek.</div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setConvertOpen(null)}>Vazgeç</Button>
                <Button onClick={submitConvert} data-testid="convert-submit">Güncelle</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!printData} onOpenChange={(o) => !o && setPrintData(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>İzin Formu</DialogTitle>
            <DialogDescription className="no-print">İzin formu yazdırma önizlemesi — Yazdır butonu ile A4 tek sayfaya sığacak şekilde çıktı alabilirsiniz.</DialogDescription>
          </DialogHeader>
          {printData && (
            <div>
              <div className="printable bg-white text-black p-8 rounded border border-border">
                <div className="text-center mb-6 print-block print-header">
                  <div className="font-display text-2xl font-bold print-title">COLOSSAE HOTEL</div>
                  <div className="text-sm uppercase tracking-[0.3em] print-subtitle">İZİN FORMU</div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm print-block">
                  <div><b>Ad Soyad:</b> {printData.user_name}</div>
                  <div><b>Departman:</b> {printData.department}</div>
                  <div><b>Talep Tarihi:</b> {printData.requested_at?.slice(0,10)}</div>
                  <div><b>Onay Tarihi:</b> {printData.approved_at?.slice(0,10) || "-"}</div>
                  {printData.leave_type === "hourly" ? (
                    <>
                      <div><b>İzin Tarihi:</b> {printData.start_date?.slice(0,10)}</div>
                      <div><b>Saat Aralığı:</b> {(printData.start_time || "-")} – {(printData.end_time || "-")}</div>
                      <div><b>İzin Türü:</b> {types[printData.leave_type]?.label}</div>
                    </>
                  ) : (
                    <>
                      <div><b>İzin Başlangıcı:</b> {printData.start_date?.slice(0,10)}</div>
                      <div><b>İş Başı Tarihi:</b> {printData.end_date?.slice(0,10)}</div>
                      <div className="col-span-2 text-xs text-muted-foreground">Son izin günü: {(() => {
                        try {
                          const e = parseLocalDate(printData.end_date?.slice(0,10)); if (!e) return "-";
                          const prev = new Date(e); prev.setDate(prev.getDate() - 1);
                          return fmtLocalDate(prev);
                        } catch { return "-"; }
                      })()}</div>
                      <div><b>Gün Sayısı:</b> {printData.day_count}</div>
                      <div><b>İzin Türü:</b> {types[printData.leave_type]?.label}</div>
                    </>
                  )}
                  {printData.leave_type === "annual" && (printData.advance_payment > 0 || printData.daily_rate > 0) && (
                    <div className="col-span-2 rounded-md bg-emerald-50 border border-emerald-300 p-2 text-sm text-emerald-900 print-block">
                      <b>Avans Ödemesi:</b> Günlük {Number(printData.daily_rate || 0).toFixed(2)} ₺ × {printData.day_count} gün = <b>{Number(printData.advance_payment || 0).toLocaleString("tr-TR")} ₺</b>
                      <div className="text-[11px]">(Yıllık izne çıkmadan önce personele ödenmesi gereken net avans tutarı — Maaş / 30 × gün.)</div>
                    </div>
                  )}
                  <div className="col-span-2"><b>Hafta tatili / resmi tatil içeriyor:</b> {printData.includes_weekend_or_holiday ? "Evet" : "Hayır"}</div>
                  {(printData.off_dates?.length > 0 || printData.holidays_in_range?.length > 0) && (
                    <div className="col-span-2 print-block">
                      <b>İzinden düşülmeyen tarihler:</b>
                      <ul className="list-disc pl-5 mt-1 text-xs">
                        {(printData.off_dates || []).map((d) => (
                          <li key={`o-${d}`}>{d}{printData.off_dates_labels?.[d] ? ` — ${printData.off_dates_labels[d]}` : " (hafta tatili / kişisel tatil)"}</li>
                        ))}
                        {(printData.holidays_in_range || []).map((h) => (
                          <li key={`h-${h.date}`}>{h.date} — {h.title}{h.is_half_day ? " (yarım gün)" : ""}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="col-span-2"><b>Açıklama:</b> {printData.reason || "-"}</div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-8 border-t pt-4 print-signatures print-sig-row print-block">
                  <div><div className="text-xs uppercase text-muted-foreground">Personel İmzası</div>{printData.staff_signature && <img src={printData.staff_signature} className="h-16" alt="staff" />}</div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">Departman Müdürü İmzası</div>
                    {printData.manager_signature && <img src={printData.manager_signature} className="h-16" alt="manager" />}
                    {printData.manager_name && <div className="text-[11px] mt-1">{printData.manager_name}</div>}
                    {printData.manager_on_behalf_note && <div className="text-[10px] italic text-muted-foreground">({printData.manager_on_behalf_note})</div>}
                  </div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">İnsan Kaynakları Müdürü İmzası</div>
                    {printData.admin_signature && <img src={printData.admin_signature} className="h-16" alt="admin" />}
                    {printData.admin_name && <div className="text-[11px] mt-1">{printData.admin_name}</div>}
                    {printData.admin_on_behalf_note && <div className="text-[10px] italic text-muted-foreground">({printData.admin_on_behalf_note})</div>}
                  </div>
                </div>
                {printData.leave_type === "annual" && printData.banking && (printData.banking.iban || printData.banking.bank_name) && (
                  <div className="mt-4 border-t pt-4 print-section print-block" data-testid="print-payment-info">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 font-semibold">Ödeme Bilgileri</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><b>Banka:</b> {printData.banking.bank_name || "-"}</div>
                      <div><b>Hesap Sahibi:</b> {printData.banking.account_holder_name || printData.user_name}</div>
                      <div className="col-span-2 font-mono text-xs">
                        <b className="font-sans not-italic text-sm">IBAN:</b>{" "}
                        {printData.banking.iban ? printData.banking.iban.replace(/(.{4})/g, "$1 ").trim() : "-"}
                      </div>
                      <div><b>Hesap No:</b> {printData.banking.account_no || "-"}</div>
                      <div><b>Şube Kodu:</b> {printData.banking.branch_code || "-"}</div>
                    </div>
                  </div>
                )}
                {printData.report_url && (
                  <div className="mt-4 border-t pt-4 print-section" data-testid="print-report-attachment">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 font-semibold">Ekli Belge</div>
                    <div className="flex flex-col items-start gap-2">
                      <img
                        src={`${API}${printData.report_url}`}
                        alt="İzin belgesi"
                        className="max-w-full max-h-72 border border-border rounded print-report-thumb"
                      />
                      <a href={`${API}${printData.report_url}`} target="_blank" rel="noreferrer" className="text-xs text-primary underline print:hidden">Yeni sekmede aç</a>
                      <div className="hidden print:block text-[10px] text-muted-foreground">📎 Ekli belge sisteme yüklenmiştir (yazıcı çıktısında ayrı sayfaya sığmadığı için gösterilmiyor).</div>
                    </div>
                  </div>
                )}
                {printData.leave_type === "annual" && printData.board_signatures?.length > 0 && (
                  <div className="mt-4 border-t pt-4 print-section print-sig-row print-block">
                    <div className="text-xs uppercase text-muted-foreground mb-2">Yıllık İzin Kurulu İmzaları</div>
                    <div className="grid grid-cols-3 gap-4">
                      {printData.board_signatures.map((b) => (
                        <div key={b.user_id || b.at}><div className="text-xs">{b.name}</div><img src={b.signature} className="h-14" alt="board" /></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={() => window.print()} data-testid="print-btn"><Printer size={16} className="mr-1" />Yazdır</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Belge doğrulama detay modal'ı */}
      <Dialog open={!!docVerifyOpen} onOpenChange={(o) => !o && setDocVerifyOpen(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Belge Doğrulama Detayı</DialogTitle>
          </DialogHeader>
          {docVerifyOpen && (
            <div className="space-y-3">
              <div className="rounded-md bg-muted/40 p-2 text-sm">
                <div className="font-medium">{docVerifyOpen.user_name} — {types[docVerifyOpen.leave_type]?.label}</div>
                <div className="text-xs text-muted-foreground">
                  {docVerifyOpen.start_date?.slice(0, 10)} → {docVerifyOpen.end_date?.slice(0, 10)} · {docVerifyOpen.day_count} gün
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <DocVerificationBadge verdict={docVerifyOpen.doc_verification} />
                <a
                  href={`${API}${docVerifyOpen.report_url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary underline"
                  data-testid="dv-open-doc"
                >
                  Belgeyi Aç
                </a>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    // Ayrı bir yazdırma penceresi aç — belgeyi tam sayfa göster
                    const w = window.open("", "_blank", "width=800,height=1000");
                    if (!w) { toast.error("Popup engelli. Tarayıcı ayarlarından izin verin."); return; }
                    w.document.write(`<!doctype html><html><head><title>İzin Belgesi</title>
                      <style>
                        body{margin:0;padding:24px;font-family:system-ui,sans-serif;background:#fff}
                        .hdr{border-bottom:2px solid #0f172a;padding-bottom:12px;margin-bottom:16px}
                        .hdr h1{margin:0;font-size:20px}
                        .hdr p{margin:4px 0 0;font-size:12px;color:#64748b}
                        .meta{display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:12px;margin-bottom:16px}
                        .meta b{display:block;color:#64748b;font-size:10px;text-transform:uppercase;letter-spacing:1px}
                        img{max-width:100%;height:auto;border:1px solid #e2e8f0;border-radius:6px}
                        @media print { .no-print{display:none} }
                      </style></head><body>
                      <div class="hdr">
                        <h1>İzin Belgesi — ${docVerifyOpen.user_name}</h1>
                        <p>Colossae Personel · ${new Date().toLocaleString("tr-TR")}</p>
                      </div>
                      <div class="meta">
                        <div><b>İzin Türü</b>${types[docVerifyOpen.leave_type]?.label || docVerifyOpen.leave_type}</div>
                        <div><b>Personel</b>${docVerifyOpen.user_name}</div>
                        <div><b>Başlangıç</b>${(docVerifyOpen.start_date||"").slice(0,10)}</div>
                        <div><b>İş Başı</b>${(docVerifyOpen.end_date||"").slice(0,10)}</div>
                        <div><b>Gün</b>${docVerifyOpen.day_count}</div>
                        <div><b>Talep Tarihi</b>${(docVerifyOpen.requested_at||"").slice(0,10)}</div>
                      </div>
                      <img src="${API}${docVerifyOpen.report_url}" alt="İzin belgesi"/>
                      <p class="no-print" style="margin-top:16px"><button onclick="window.print()">Yazdır</button></p>
                      <script>window.addEventListener('load',()=>setTimeout(()=>window.print(),400))</script>
                    </body></html>`);
                    w.document.close();
                  }}
                  data-testid="dv-print-doc"
                >
                  Belgeyi Yazdır
                </Button>
                {["admin", "hr", "gm", "manager"].includes(user?.role) && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={rerunning || docVerifyOpen.doc_verification?.status === "running"}
                    onClick={async () => {
                      setRerunning(true);
                      // Optimistic: flip status to 'running' immediately so the user
                      // sees the pulsing "Doğrulanıyor…" badge without waiting for
                      // the background task to write it to Mongo.
                      const optimistic = { ...docVerifyOpen, doc_verification: { status: "running", started_at: new Date().toISOString() } };
                      setDocVerifyOpen(optimistic);
                      setLeaves((prev) => prev.map((l) => l.id === optimistic.id ? optimistic : l));
                      try {
                        await api.post(`/leaves/${docVerifyOpen.id}/verify-document`);
                        toast.success("Doğrulama yeniden başlatıldı. Birkaç saniye içinde sonuç güncellenecek.");
                        setTimeout(() => { load(); }, 8000);
                      } catch (e) {
                        toast.error(humanizeAxiosError(e));
                        // Restore original verdict on failure
                        setDocVerifyOpen(docVerifyOpen);
                      } finally { setRerunning(false); }
                    }}
                    data-testid="dv-rerun"
                  >
                    {rerunning ? "Başlatılıyor…" : "Yeniden Doğrula"}
                  </Button>
                )}
              </div>

              {!docVerifyOpen.doc_verification && (
                <div className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
                  Doğrulama henüz tamamlanmadı ya da bu izin türü için doğrulama gerekmiyor.
                </div>
              )}

              {docVerifyOpen.doc_verification && (
                <>
                  {docVerifyOpen.doc_verification.summary && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Özet</div>
                      <div className="text-sm rounded-md bg-muted/30 p-2">{docVerifyOpen.doc_verification.summary}</div>
                    </div>
                  )}

                  {(docVerifyOpen.doc_verification.flags || []).length > 0 && (
                    <div data-testid="dv-flags">
                      <div className="text-xs uppercase tracking-wider text-amber-700 dark:text-amber-400 font-semibold mb-1">Dikkat Edilmesi Gerekenler</div>
                      <ul className="space-y-1">
                        {docVerifyOpen.doc_verification.flags.map((f, i) => (
                          <li key={i} className="text-sm rounded-md bg-amber-500/10 border border-amber-500/40 p-2 text-amber-900 dark:text-amber-200">• {f}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {docVerifyOpen.doc_verification.matched_dates?.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Belgede Bulunan Eşleşen Tarihler</div>
                      <div className="flex flex-wrap gap-1">
                        {docVerifyOpen.doc_verification.matched_dates.map((d) => (
                          <Badge key={d} variant="secondary" className="font-mono text-xs">{d}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {docVerifyOpen.doc_verification.raw && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Ham veri (belgeden çıkarılan tüm alanlar)</summary>
                      <div className="mt-2 space-y-1 font-mono rounded-md bg-muted/40 p-2 max-h-56 overflow-y-auto">
                        <div><b>Belge türü:</b> {docVerifyOpen.doc_verification.raw.document_type || "?"}</div>
                        <div><b>Kurum:</b> {docVerifyOpen.doc_verification.raw.institution || "?"}</div>
                        <div><b>Bulunan tüm tarihler:</b> {(docVerifyOpen.doc_verification.raw.dates_found || []).join(", ") || "—"}</div>
                        <div><b>İsimler:</b> {(docVerifyOpen.doc_verification.raw.names_found || []).join(", ") || "—"}</div>
                        <div><b>Anahtar kelimeler:</b> {(docVerifyOpen.doc_verification.raw.keywords || []).join(", ") || "—"}</div>
                      </div>
                    </details>
                  )}

                  <div className="text-[11px] text-muted-foreground border-t border-border pt-2 leading-relaxed">
                    ⚡ Bu doğrulama <b>Gemini vision (yapay zeka)</b> tarafından yapıldı. Sonuç kesin değildir — nihai karar İK/Admin'e aittir.
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
